import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LiveChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chatStatus, setChatStatus] = useState('online'); // online, busy, offline
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);

  const initialMessages = [
    {
      id: 1,
      type: 'bot',
      content: '¡Hola! Soy el asistente virtual de Orbiax. ¿En qué puedo ayudarte hoy?',
      timestamp: new Date(),
      sender: 'Asistente Virtual'
    }
  ];

  const quickReplies = [
    'Quiero agendar una consulta',
    'Información sobre precios',
    'Tipos de ERP disponibles',
    'Hablar con un consultor'
  ];

  const autoResponses = {
    'consulta': 'Perfecto! Puedes agendar tu consulta gratuita directamente desde nuestra página. ¿Te gustaría que te redirija al formulario de agendamiento?',
    'precio': 'Nuestros precios varían según las necesidades específicas de tu empresa. Te recomiendo agendar una consulta gratuita donde podremos darte una cotización personalizada.',
    'erp': 'Trabajamos con SAP, Microsoft Dynamics 365, Oracle NetSuite, Odoo y también desarrollamos soluciones personalizadas. ¿Tienes alguna preferencia específica?',
    'consultor': 'Te conectaré con uno de nuestros consultores especializados. Por favor, déjame tu información de contacto y te llamaremos en los próximos minutos.',
    'horario': 'Nuestro horario de atención es de Lunes a Viernes de 9:00 AM a 6:00 PM, y Sábados de 10:00 AM a 2:00 PM.',
    'default': 'Gracias por tu mensaje. Un consultor especializado se pondrá en contacto contigo pronto. ¿Hay algo más en lo que pueda ayudarte? '
  };

  useEffect(() => {
    if (messages.length === 0) {
      setMessages(initialMessages);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!isOpen && messages.length > initialMessages.length) {
      setUnreadCount(messages.length - initialMessages.length);
    } else {
      setUnreadCount(0);
    }
  }, [isOpen, messages.length]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const generateBotResponse = (userMessage) => {
    const message = userMessage.toLowerCase();

    if (message.includes('consulta') || message.includes('agendar')) {
      return autoResponses.consulta;
    } else if (message.includes('precio') || message.includes('costo') || message.includes('cotización')) {
      return autoResponses.precio;
    } else if (message.includes('erp') || message.includes('sistema')) {
      return autoResponses.erp;
    } else if (message.includes('consultor') || message.includes('humano') || message.includes('persona')) {
      return autoResponses.consultor;
    } else if (message.includes('horario') || message.includes('hora')) {
      return autoResponses.horario;
    } else {
      return autoResponses.default;
    }
  };

  const handleSendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageText,
      timestamp: new Date(),
      sender: 'Tú'
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    // Simulate bot response delay
    setTimeout(() => {
      const botResponse = {
        id: Date.now() + 1,
        type: 'bot',
        content: generateBotResponse(messageText),
        timestamp: new Date(),
        sender: 'Asistente Virtual'
      };

      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1500);
  };

  const handleQuickReply = (reply) => {
    handleSendMessage(reply);
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = () => {
    switch (chatStatus) {
      case 'online': return 'bg-green-500';
      case 'busy': return 'bg-yellow-500';
      case 'offline': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = () => {
    switch (chatStatus) {
      case 'online': return 'En línea';
      case 'busy': return 'Ocupado';
      case 'offline': return 'Fuera de línea';
      default: return 'Desconocido';
    }
  };

  return (
    <>
      {/* Chat Widget Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={toggleChat}
          className={`relative w-16 h-16 rounded-full shadow-lg transition-all duration-300 hover:scale-110 ${isOpen
              ? 'bg-red-500 hover:bg-red-600' : 'bg-black hover:opacity-80'
            }`}
        >
          <Icon
            name={isOpen ? "X" : "MessageCircle"}
            size={24}
            className="text-white"
          />

          {/* Unread Badge */}
          {unreadCount > 0 && !isOpen && (
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-xs font-bold text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            </div>
          )}

          {/* Online Status Indicator */}
          <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${getStatusColor()}`} />
        </button>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-96 h-[500px] bg-white border border-black/10 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden">
          {/* Chat Header */}
          <div className="bg-black p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Icon name="Headphones" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold">Soporte Orbiax</h3>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
                    <span className="text-xs opacity-90">{getStatusText()}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={toggleChat}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <Icon name="Minus" size={20} />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-background">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${message.type === 'user' ? 'bg-primary text-white' : 'bg-card border border-border text-foreground'
                    }`}
                >
                  <p className="text-sm">{message.content}</p>
                  <p className={`text-xs mt-1 ${message.type === 'user' ? 'text-white/70' : 'text-muted-foreground'
                    }`}>
                    {formatTime(message.timestamp)}
                  </p>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-card border border-border p-3 rounded-lg">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                    <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          {messages.length <= initialMessages.length && (
            <div className="p-4 border-t border-border bg-muted/30">
              <p className="text-xs text-muted-foreground mb-2">Respuestas rápidas:</p>
              <div className="flex flex-wrap gap-2">
                {quickReplies.map((reply, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickReply(reply)}
                    className="text-xs px-3 py-1 bg-black/5 text-black rounded-full hover:bg-black/10 transition-colors"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="p-4 border-t border-border bg-card">
            <div className="flex space-x-2">
              <Input
                type="text"
                placeholder="Escribe tu mensaje..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                className="flex-1"
              />
              <Button
                variant="default"
                size="icon"
                onClick={() => handleSendMessage()}
                disabled={!inputMessage.trim()}
                className="bg-black hover:opacity-80"
              >
                <Icon name="Send" size={16} />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Respuesta típica en menos de 2 minutos
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default LiveChatWidget;